import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Space,
  Switch,
  Card,
  Row,
  Col,
  Table,
  Tag,
  Statistic,
  Drawer,
  Typography,
  App,
  Descriptions,
  Alert,
  Button,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { GenerationRunLog, GenerationRunTriggerType } from '@mygate/common';
import { autoGenerationService } from '@/services/autoGeneration';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

export function AutoGenerationTab() {
  const queryClient = useQueryClient();
  const { modal, message } = App.useApp();
  const [selectedRun, setSelectedRun] = useState<GenerationRunLog | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Fetch kill switch state
  const {
    data: setting,
    isLoading: settingLoading,
    isError: settingError,
    refetch: refetchSetting,
  } = useQuery({
    queryKey: ['auto-generation-setting'],
    queryFn: () => autoGenerationService.getAutoGenerationSetting(),
    refetchInterval: 30000,
  });

  // Fetch stats
  const {
    data: stats,
    isLoading: statsLoading,
    isError: statsError,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ['generation-run-stats'],
    queryFn: () => autoGenerationService.getGenerationRunStats(),
    refetchInterval: 30000,
  });

  // Fetch generation runs
  const {
    data: runsData,
    isLoading: runsLoading,
    isError: runsError,
    refetch: refetchRuns,
  } = useQuery({
    queryKey: ['generation-runs', page, pageSize],
    queryFn: () => autoGenerationService.getGenerationRuns({ page, limit: pageSize }),
    refetchInterval: 30000,
  });

  const hasError = settingError || statsError || runsError;

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) => autoGenerationService.toggleAutoGeneration(enabled),
    onSuccess: data => {
      if (!data) return;
      message.success(`Auto-processing ${data.enabled ? 'enabled' : 'disabled'}`);
      queryClient.invalidateQueries({ queryKey: ['auto-generation-setting'] });
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to toggle setting';
      message.error(errorMessage);
    },
  });

  const handleToggle = (checked: boolean) => {
    modal.confirm({
      title: checked ? 'Enable Auto-Processing' : 'Disable Auto-Processing',
      content: checked
        ? 'Standing orders will be automatically processed daily at 2:00 AM CET.'
        : 'Automatic standing order processing will be stopped. Manual processing will still be available.',
      okText: checked ? 'Enable' : 'Disable',
      okType: checked ? 'primary' : 'danger',
      onOk: () => toggleMutation.mutate(checked),
    });
  };

  const getTriggerTypeColor = (type: GenerationRunTriggerType) => {
    switch (type) {
      case GenerationRunTriggerType.SCHEDULED:
        return 'blue';
      case GenerationRunTriggerType.MANUAL:
        return 'green';
      case GenerationRunTriggerType.CATCH_UP:
        return 'orange';
      default:
        return 'default';
    }
  };

  const columns: ColumnsType<GenerationRunLog> = useMemo(
    () => [
      {
        title: 'Run Date',
        dataIndex: 'runDate',
        key: 'runDate',
        width: 160,
        render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
      },
      {
        title: 'Trigger',
        dataIndex: 'triggerType',
        key: 'triggerType',
        width: 120,
        render: (type: GenerationRunTriggerType) => <Tag color={getTriggerTypeColor(type)}>{type}</Tag>,
      },
      {
        title: 'Target Date',
        dataIndex: 'targetDate',
        key: 'targetDate',
        width: 120,
        render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
      },
      {
        title: 'Generated',
        dataIndex: 'generatedCount',
        key: 'generatedCount',
        width: 100,
        align: 'right',
        render: (count: number) => (
          <Text strong style={{ color: count > 0 ? '#52c41a' : undefined }}>
            {count}
          </Text>
        ),
      },
      {
        title: 'Skipped',
        dataIndex: 'skippedCount',
        key: 'skippedCount',
        width: 100,
        align: 'right',
      },
      {
        title: 'Errors',
        dataIndex: 'errorCount',
        key: 'errorCount',
        width: 80,
        align: 'right',
        render: (count: number) => <Text style={{ color: count > 0 ? '#ff4d4f' : undefined }}>{count}</Text>,
      },
      {
        title: 'Amount (EUR)',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        width: 120,
        align: 'right',
        render: (amount: string) =>
          new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(parseFloat(amount || '0')),
      },
      {
        title: 'Duration',
        dataIndex: 'duration',
        key: 'duration',
        width: 100,
        render: (ms: number) => `${(ms / 1000).toFixed(1)}s`,
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 80,
        render: (_, record) =>
          record.errorCount > 0 ? (
            <Button
              type="link"
              className="table-cell-link"
              onClick={() => {
                setSelectedRun(record);
                setDrawerVisible(true);
              }}
            >
              Details
            </Button>
          ) : null,
      },
    ],
    [],
  );

  const todayStats = stats?.today;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {hasError && (
        <Alert
          message="Failed to load data"
          description="Some data could not be loaded. Please try again."
          type="error"
          showIcon
          action={
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => {
                refetchSetting();
                refetchStats();
                refetchRuns();
              }}
            >
              Retry
            </Button>
          }
        />
      )}
      {/* Kill Switch */}
      <Card>
        <Space wrap align="center" size="middle">
          <Switch
            checked={setting?.enabled ?? true}
            onChange={handleToggle}
            loading={settingLoading || toggleMutation.isPending}
          />
          <div>
            <Title level={5} style={{ margin: 0 }}>
              Automatic Standing Order Processing
            </Title>
            <Text type="secondary">
              {setting?.enabled !== false ? 'Active - Runs daily at 2:00 AM CET' : 'Disabled - Manual processing only'}
            </Text>
            {setting?.updatedBy && (
              <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                Last changed by: {setting.updatedBy} on{' '}
                {setting.updatedAt ? dayjs(setting.updatedAt).format('YYYY-MM-DD HH:mm') : 'N/A'}
              </Text>
            )}
          </div>
        </Space>
      </Card>

      {/* Stats Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12} xl={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Generated Today"
              value={todayStats?.totalGenerated ?? 0}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Auto-Approved Today"
              value={todayStats?.totalAutoApproved ?? 0}
              prefix={<ClockCircleOutlined style={{ color: '#1890ff' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Errors Today"
              value={todayStats?.totalErrors ?? 0}
              prefix={<CloseCircleOutlined style={{ color: todayStats?.totalErrors ? '#ff4d4f' : '#d9d9d9' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} md={12} xl={6}>
          <Card loading={statsLoading}>
            <Statistic
              title="Amount Today (EUR)"
              value={parseFloat(todayStats?.totalAmount ?? '0')}
              precision={2}
              prefix={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* 7-day / 30-day summary */}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card title="Last 7 Days" size="small" loading={statsLoading}>
            <Row gutter={8}>
              <Col xs={24} md={12} xl={8}>
                <Statistic title="Runs" value={stats?.last7Days?.totalRuns ?? 0} valueStyle={{ fontSize: 18 }} />
              </Col>
              <Col xs={24} md={12} xl={8}>
                <Statistic
                  title="Generated"
                  value={stats?.last7Days?.totalGenerated ?? 0}
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
              <Col xs={24} md={12} xl={8}>
                <Statistic
                  title="Errors"
                  value={stats?.last7Days?.totalErrors ?? 0}
                  valueStyle={{ fontSize: 18, color: (stats?.last7Days?.totalErrors ?? 0) > 0 ? '#ff4d4f' : undefined }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} xl={12}>
          <Card title="Last 30 Days" size="small" loading={statsLoading}>
            <Row gutter={8}>
              <Col xs={24} md={12} xl={8}>
                <Statistic title="Runs" value={stats?.last30Days?.totalRuns ?? 0} valueStyle={{ fontSize: 18 }} />
              </Col>
              <Col xs={24} md={12} xl={8}>
                <Statistic
                  title="Generated"
                  value={stats?.last30Days?.totalGenerated ?? 0}
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
              <Col xs={24} md={12} xl={8}>
                <Statistic
                  title="Errors"
                  value={stats?.last30Days?.totalErrors ?? 0}
                  valueStyle={{
                    fontSize: 18,
                    color: (stats?.last30Days?.totalErrors ?? 0) > 0 ? '#ff4d4f' : undefined,
                  }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Generation Run History Table */}
      <Table
        scroll={{ x: 980 }}
        columns={columns}
        dataSource={runsData?.data ?? []}
        rowKey="id"
        loading={runsLoading}
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: false,
          total: runsData?.total ?? 0,
          showTotal: total => `Total ${total} runs`,
          onChange: p => setPage(p),
        }}
      />

      {/* Error Details Drawer */}
      <Drawer
        title="Run Error Details"
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setSelectedRun(null);
        }}
        width={600}
      >
        {selectedRun && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Descriptions column={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 }} bordered size="small">
              <Descriptions.Item label="Target Date">
                {dayjs(selectedRun.targetDate).format('YYYY-MM-DD')}
              </Descriptions.Item>
              <Descriptions.Item label="Trigger">{selectedRun.triggerType}</Descriptions.Item>
              <Descriptions.Item label="Generated">{selectedRun.generatedCount}</Descriptions.Item>
              <Descriptions.Item label="Errors">{selectedRun.errorCount}</Descriptions.Item>
            </Descriptions>

            <Title level={5}>Errors ({selectedRun.errors.length})</Title>
            <Table
              scroll={{ x: 'max-content' }}
              dataSource={selectedRun.errors}
              rowKey={(_, idx) => String(idx)}
              size="small"
              pagination={false}
              columns={[
                {
                  title: 'Standing Order ID',
                  dataIndex: 'standingOrderId',
                  key: 'standingOrderId',
                  width: 200,
                  ellipsis: true,
                },
                {
                  title: 'User ID',
                  dataIndex: 'userId',
                  key: 'userId',
                  width: 200,
                  ellipsis: true,
                  render: (val: string) => val || '-',
                },
                {
                  title: 'Error',
                  dataIndex: 'error',
                  key: 'error',
                },
              ]}
            />
          </Space>
        )}
      </Drawer>
    </Space>
  );
}
